import { EyeOutlined, RetweetOutlined } from '@ant-design/icons';
import { ManGenderIcon } from '@assets/icons/manGender';
import { NeutralGenderIcon } from '@assets/icons/neutralGender';
import { WomanGenderIcon } from '@assets/icons/womanGender';
import { Image, message, Progress, Row } from 'antd';
import { memo, MouseEvent, ReactNode, useState } from 'react';
import { ControllerProps, get, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { useUploadContentsFile } from 'src/features/programsV2/hooks/useUploadFileContents';
import { ContentStepForm } from 'src/features/programsV2/schemas/contentStepSchema';
import { ContentGender } from 'src/features/programsV2/types/programModulesAndContents';
import { showErrorModal } from 'src/features/programsV2/utils/modalHelpers';
import { isFileSizeLess } from 'src/features/programsV2/utils/validateFileSize';

import { TransparentButton } from '../../ui/TransparentButton';
import UploadDrawer, { DrawerFileProps } from '../../UploadDrawer';

export interface ThumbPreviewProps {
  genderType: ContentGender;
  genderThumbName: ControllerProps<ContentStepForm>['name'];
  disabled?: boolean;
}

export const ThumbPreview = memo<ThumbPreviewProps>(
  ({ genderType, genderThumbName, disabled }) => {
    const params = useParams<{ id: string }>();
    const programId = Number(params.id);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const {
      setValue,
      watch,
      formState: { errors },
    } = useFormContext<ContentStepForm>();

    const { uploadFile, progress, isUploading } = useUploadContentsFile();

    const genderThumbFile = watch(genderThumbName);

    const handleUploadImage = async (file: DrawerFileProps) => {
      const formData = new FormData();
      formData.append('file', file.file);

      const isValidSize = isFileSizeLess(file.file);
      if (!isValidSize) {
        return;
      }

      try {
        const data = await uploadFile({ file: formData, programId });
        setValue(
          genderThumbName,
          {
            preview: data?.imageUrl,
            file: '',
          },
          { shouldValidate: true }
        );
        message.success('Arquivo adicionado com sucesso!');
      } catch (error) {
        showErrorModal({
          title: 'Tivemos um problema...',
          content: 'Não foi possível salvar este arquivo',
        });
      }
    };

    const fallbackThumbMapping: Record<ContentGender, ReactNode> = {
      man: <ManGenderIcon />,
      woman: <WomanGenderIcon />,
      neutral: <NeutralGenderIcon />,
    };

    const handlePreviewImage = (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setIsPreviewOpen(true);
    };

    const handleChangeImage = (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setIsDrawerOpen(true);
    };

    const error = get(errors, `${genderThumbName}.preview`);

    return (
      <>
        <div style={{ position: 'relative', width: 120, height: 72 }}>
          {(genderThumbFile as any)?.preview ? (
            <ImageContainer isDisabled={disabled}>
              <Image
                src={(genderThumbFile as any).preview}
                alt='Preview'
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                }}
                style={{ objectFit: 'cover' }}
                width={120}
                height={72}
                preview={{
                  visible: isPreviewOpen,
                  onVisibleChange: visible => setIsPreviewOpen(visible),
                  mask: (
                    <Row style={{ gap: 4 }}>
                      <TransparentButton
                        icon={<EyeOutlined />}
                        onClick={handlePreviewImage}
                      />
                      <TransparentButton
                        icon={<RetweetOutlined />}
                        onClick={handleChangeImage}
                      />
                    </Row>
                  ),
                }}
              />
            </ImageContainer>
          ) : (
            <>
              <ImageContainer
                onClick={() => setIsDrawerOpen(true)}
                isError={!!error}
                isDisabled={disabled}
              >
                {isUploading ? (
                  <Progress
                    percent={progress}
                    status={'active'}
                    size='small'
                    showInfo={false}
                    style={{ marginInline: 10 }}
                  />
                ) : (
                  fallbackThumbMapping[genderType]
                )}
              </ImageContainer>
            </>
          )}
        </div>

        {isDrawerOpen && (
          <UploadDrawer
            visible={isDrawerOpen}
            onChange={file => handleUploadImage(file)}
            onClose={() => setIsDrawerOpen(false)}
            contentType='image'
            uploadMessage='.png, .webp, .jpg ou .gif 1280x720pixel (@2x)'
          />
        )}
      </>
    );
  }
);

const ImageContainer = styled.div<{ isError?: boolean; isDisabled?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
  border: 1px solid ${({ isError }) => (isError ? '#A61D25' : '#303030')};
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;

  position: relative;

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      cursor: not-allowed;
      pointer-events: none;
      background-color: #2c2c2c;
    `};
`;
